(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  class JCCarousel extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.track = this.querySelector('.jc-carousel__track');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dots = this.querySelector('[data-dots]');
      this.index = 0;
      this.buttons = [];
      this.prev?.addEventListener('click', () => this.go(this.index - 1), options);
      this.next?.addEventListener('click', () => this.go(this.index + 1), options);
      this.track.addEventListener('scroll', () => this.sync(), { passive: true, ...options });
      this.track.addEventListener('pointerdown', () => this.stop(), options);
      this.track.addEventListener('wheel', () => this.stop(), { passive: true, ...options });
      this.track.addEventListener('keydown', e => {
        if (e.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
        e.preventDefault();
        this.go(e.key === 'Home' ? 0 : e.key === 'End' ? this.maxIndex : this.index + (e.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this.track);
      this.measure();
    }
    measure() {
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.step = (this.slides[0]?.getBoundingClientRect().width || 1) + gap;
      this.maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.maxIndex = Math.ceil(this.maxScroll / this.step - .01);
      if (this.dots && this.buttons.length !== this.maxIndex + 1) {
        this.dots.replaceChildren();
        this.buttons = Array.from({ length: this.maxIndex + 1 }, (_, i) => {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'jc-dot';
          button.setAttribute('aria-label', `${this.dots.dataset.dotLabel} ${i + 1}`);
          button.addEventListener('click', () => this.go(i), { signal: this.abort.signal });
          this.dots.append(button); return button;
        });
        this.dots.hidden = this.maxIndex < 1;
      }
      this.querySelector('[data-controls]')?.toggleAttribute('hidden', this.maxIndex < 1);
      this.sync();
    }
    sync() {
      this.index = Math.min(this.maxIndex, Math.round(this.track.scrollLeft / this.step));
      if (this.track.scrollLeft >= this.maxScroll - 2) this.index = this.maxIndex;
      if (this.prev) this.prev.disabled = this.index <= 0;
      if (this.next) this.next.disabled = this.index >= this.maxIndex;
      this.buttons.forEach((button, i) => button.setAttribute('aria-current', String(i === this.index)));
    }
    stop() { cancelAnimationFrame(this.frame); this.track.style.scrollSnapType = ''; }
    go(index) {
      this.stop();
      const target = Math.min(this.maxScroll, Math.max(0, index) * this.step);
      if (reduced.matches) { this.track.scrollLeft = target; this.sync(); return; }
      const start = this.track.scrollLeft;
      const duration = Number(this.dataset.duration) || 400;
      const began = performance.now();
      this.track.style.scrollSnapType = 'none';
      const move = now => {
        const p = Math.min((now - began) / duration, 1);
        const eased = p < .5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
        this.track.scrollLeft = start + (target - start) * eased;
        if (p < 1) this.frame = requestAnimationFrame(move);
        else { this.track.style.scrollSnapType = ''; this.sync(); }
      };
      this.frame = requestAnimationFrame(move);
    }
    disconnectedCallback() { this.stop(); this.observer?.disconnect(); this.abort?.abort(); this.ready = false; }
  }
  class JCVideo extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true; this.abort = new AbortController(); this.manualPause = false;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.button = this.querySelector('[data-video-toggle]');
      this.mobile = matchMedia('(max-width:749px)');
      this.sources = [...this.video.querySelectorAll('source')].map(s => ({ src: s.dataset.src, type: s.type, device: s.dataset.device }));
      this.select = () => {
        const wanted = this.mobile.matches ? 'mobile' : 'desktop';
        const source = this.sources.find(s => s.device === wanted) || this.sources.find(s => !s.device) || this.sources[0];
        if (!source?.src) return;
        this.video.src = source.src; this.video.muted = true;
        if (this.button) this.button.hidden = false;
        if (this.visible && !this.manualPause && !reduced.matches) this.video.play().catch(() => {});
      };
      this.select();
      this.video.addEventListener('playing', () => { this.classList.add('is-playing'); this.update(); }, { signal: this.abort.signal });
      this.video.addEventListener('pause', () => this.update(), { signal: this.abort.signal });
      this.button?.addEventListener('click', () => {
        if (this.video.paused) { this.manualPause = false; this.video.play().catch(() => {}); }
        else { this.manualPause = true; this.video.pause(); }
      }, { signal: this.abort.signal });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible && !this.manualPause && !reduced.matches) this.video.play().catch(() => {});
        else this.video.pause();
      }, { threshold: .1 });
      this.observer.observe(this);
      this.mobile.addEventListener('change', this.select, { signal: this.abort.signal });
      reduced.addEventListener('change', () => { if (reduced.matches) this.video.pause(); }, { signal: this.abort.signal });
    }
    update() {
      if (!this.button) return;
      this.button.setAttribute('aria-label', this.video.paused ? this.button.dataset.playLabel : this.button.dataset.pauseLabel);
      this.button.querySelector('[data-video-symbol]').textContent = this.video.paused ? '▶' : 'Ⅱ';
    }
    disconnectedCallback() { this.video?.pause(); this.observer?.disconnect(); this.abort?.abort(); this.ready = false; }
  }
  class JCHero extends JCVideo {}
  class JCUGC extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true; this.abort = new AbortController();
      this.track = this.querySelector('.jb-ugc__track');
      this.cards = [...this.track.children];
      if (this.cards.length < 2) return;
      this.cards.forEach(card => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true'); clone.dataset.clone = 'true';
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach(e => e.removeAttribute('id'));
        clone.querySelectorAll('a,button,[tabindex]').forEach(e => e.tabIndex = -1);
        this.track.append(clone);
      });
      this.position = 0; this.last = 0; this.hovered = false; this.focused = false; this.touching = false; this.resumeAt = 0;
      this.paused = this.dataset.autoplay !== 'true';
      this.button = this.querySelector('[data-ugc-toggle]');
      this.button?.addEventListener('click', () => { this.paused = !this.paused; this.updateButton(); }, { signal: this.abort.signal });
      this.updateButton();
      const on = (name, fn, passive=false) => this.track.addEventListener(name, fn, { signal: this.abort.signal, passive });
      on('pointerenter', e => { if (e.pointerType === 'mouse') this.hovered = true; });
      on('pointerleave', () => { this.hovered = false; });
      on('focusin', () => { this.focused = true; });
      on('focusout', () => { this.focused = false; });
      on('touchstart', () => { this.touching = true; }, true);
      const release = () => { this.touching = false; this.resumeAt = performance.now() + 2000; };
      on('touchend', release, true); on('touchcancel', release, true);
      on('wheel', () => { this.resumeAt = performance.now() + 2000; }, true);
      on('scroll', () => { if (this.hovered || this.focused || this.touching || performance.now() < this.resumeAt) this.position = this.track.scrollLeft; }, true);
      this.resize = new ResizeObserver(() => {
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.step = this.cards[0].getBoundingClientRect().width + gap;
        this.cycle = this.step * this.cards.length;
        this.position %= this.cycle || 1;
      });
      this.resize.observe(this.track);
      this.intersection = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; });
      this.intersection.observe(this);
      const tick = now => {
        const elapsed = Math.min(now - (this.last || now), 100); this.last = now;
        if (!this.paused && !this.hovered && !this.focused && !this.touching && !reduced.matches && !document.hidden && this.visible && now >= this.resumeAt && this.cycle) {
          this.position = (this.position + elapsed * this.step / (Number(this.dataset.seconds || 5) * 1000)) % this.cycle;
          this.track.scrollLeft = this.position;
        }
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
      document.addEventListener('shopify:block:select', e => {
        if (!this.contains(e.target)) return;
        this.paused = true; this.updateButton();
        this.track.scrollLeft = e.target.offsetLeft;
      }, { signal: this.abort.signal });
    }
    updateButton() { if (this.button) { this.button.textContent = this.paused ? this.button.dataset.playLabel : this.button.dataset.pauseLabel; this.button.setAttribute('aria-pressed', String(this.paused)); } }
    disconnectedCallback() { cancelAnimationFrame(this.frame); this.resize?.disconnect(); this.intersection?.disconnect(); this.abort?.abort(); this.track?.querySelectorAll('[data-clone]').forEach(e => e.remove()); this.ready = false; }
  }
  if (!customElements.get('jc-carousel')) customElements.define('jc-carousel', JCCarousel);
  if (!customElements.get('jc-hero')) customElements.define('jc-hero', JCHero);
  if (!customElements.get('jc-inline-video')) customElements.define('jc-inline-video', JCVideo);
  if (!customElements.get('jc-ugc')) customElements.define('jc-ugc', JCUGC);
})();
